import React from "react";
import { Text } from "@zorni/components-lib";
import { useTranslation } from "react-i18next";

import styles from "../TripView.module.css";

const numberValue = (value) => Number(value?.value ?? 0);
const currencyValue = (value) => value?.currency || "DH";
const priceValue = (value) => `${numberValue(value)} ${currencyValue(value)}`;

const formatDate = (value, locale, options) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString(locale, options);
};

const TripDatesSidebar = ({
  tripDates,
  selectedDateIndex,
  onSelectDate,
  childsMaxAge,
  babiesMaxAge,
}) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || "fr-FR";

  return (
    <aside className={styles.sidebar}>
      <div className={styles.sidebarTop}>
        <div className={styles.sidebarHeader}>
          <Text as="h4" size="md" weight="bold" className={styles.sidebarTitle}>
            {t("tripPublication.tripView.sections.datesStocks")}
          </Text>
          <Text as="p" size="xs" className={styles.sidebarHint}>
            {t("tripPublication.tripView.labels.realtimeData", { defaultValue: "Donnees temps reel" })}
          </Text>
        </div>
      </div>

      <div className={styles.dateCards}>
        {(tripDates || []).map((tripDate, index) => {
          const isActive = index === selectedDateIndex;
          const manager = tripDate?.travelManagers?.[0];

          return (
            <button
              key={tripDate?.id || `${tripDate?.startDate}-${index}`}
              type="button"
              className={[styles.dateCard, isActive ? styles.dateCardActive : ""].join(" ")}
              onClick={() => onSelectDate(index)}
            >
              {tripDate?.isCheapestDate && (
                <span className={styles.lowestPriceBadge}>
                  {t("tripPublication.publishTrip.labels.bestPrice", {
                    defaultValue: "Lowest price",
                  })}
                </span>
              )}
              <div className={styles.dateCardTop}>
                <div className={styles.dateCardHeader}>
                  <Text as="p" size="sm" weight="bold" className={styles.dateText}>
                    {`${formatDate(tripDate?.startDate, locale, { day: "2-digit", month: "short" })} - ${formatDate(tripDate?.endDate, locale, { day: "2-digit", month: "short", year: "numeric" })}`}
                  </Text>
                  <Text as="p" size="xs" className={styles.daysCountText}>
                    {t("tripPublication.tripView.labels.daysCount", { count: tripDate?.nbDays || 0 })}
                  </Text>
                </div>
                <div className={styles.adultPriceBlock}>
                  <Text as="p" size="md" weight="bold" className={styles.adultPriceValue}>
                    {priceValue(tripDate?.adultPricePerPerson)}
                  </Text>
                  <Text as="p" size="xs" className={styles.adultPriceLabel}>
                    {t("tripPublication.tripView.labels.adultPriceLabel", { defaultValue: "Prix adulte" })}
                  </Text>
                </div>
              </div>

              <div className={styles.datePriceGrid}>
                <div className={styles.priceCell}>
                  <Text as="p" size="xs" className={styles.priceCellLabel}>
                    {t("tripPublication.tripView.labels.child")}
                  </Text>
                  <Text as="p" size="sm" weight="bold" className={styles.priceCellValue}>
                    {priceValue(tripDate?.childPricePerPerson)}
                  </Text>
                </div>
                <div className={styles.priceCell}>
                  <Text as="p" size="xs" className={styles.priceCellLabel}>
                    {t("tripPublication.tripView.labels.baby")}
                  </Text>
                  <Text as="p" size="sm" weight="bold" className={styles.priceCellValue}>
                    {priceValue(tripDate?.babiesPricePerPerson)}
                  </Text>
                </div>
                <div className={[styles.priceCell, styles.stockCell].join(" ")}>
                  <Text as="p" size="xs" className={styles.stockCellLabel}>
                    {t("tripPublication.tripView.labels.stock")}
                  </Text>
                  <Text as="p" size="sm" weight="bold" className={styles.stockCellValue}>
                    {tripDate?.seatsAvailable?.initialNumberOfSeatsAvailable ?? 0}
                  </Text>
                </div>
              </div>

              <div className={styles.managerRow}>
                <span className={styles.managerAvatar}>
                  {(manager?.fullName || t("tripPublication.tripView.empty.noManager")).charAt(0)}
                </span>
                <Text as="p" size="xs" weight="bold" className={styles.managerText}>
                  {manager?.fullName || t("tripPublication.tripView.empty.noManager")}
                </Text>
              </div>
            </button>
          );
        })}

        {!tripDates?.length && (
          <Text as="p" size="sm" className={styles.emptySidebarText}>
            {t("tripPublication.tripView.empty.noDate")}
          </Text>
        )}
      </div>

      <div className={styles.sidebarMeta}>
        <div className={styles.metaCard}>
          <Text as="p" size="xs" className={[styles.metaLabel, styles.metaSmallLabel].join(" ")}>
            {t("tripPublication.tripView.labels.maxChildAge", { defaultValue: "Enfants max" })}
          </Text>
          <Text as="p" size="sm" weight="bold" className={styles.metaValueAccent}>
            {t("tripPublication.tripView.labels.ageValue", { age: childsMaxAge ?? 0 })}
          </Text>
        </div>
        <div className={styles.metaCard}>
          <Text as="p" size="xs" className={[styles.metaLabel, styles.metaSmallLabel].join(" ")}>
            {t("tripPublication.tripView.labels.maxBabyAge")}
          </Text>
          <Text as="p" size="sm" weight="bold" className={styles.metaValueAccent}>
            {t("tripPublication.tripView.labels.ageValue", { age: babiesMaxAge ?? 0 })}
          </Text>
        </div>
      </div>
    </aside>
  );
};

export default TripDatesSidebar;
